import type {Collection, CollectionStateBase, Node} from '@react-types/shared';
import type {ReactElement} from 'react';
import {useMemo} from 'react';
import {CollectionBuilder} from './CollectionBuilder';

interface CollectionOptions<T, C extends Collection<Node<T>>>
  extends Omit<CollectionStateBase<T, C>, 'children'> {
  children?: ReactElement | ReactElement[] | ((item: T) => ReactElement);
}

type CollectionFactory<T, C extends Collection<Node<T>>> = (
  node: Iterable<Node<T>>,
) => C;

export function useCollection<
  T extends object,
  C extends Collection<Node<T>> = Collection<Node<T>>,
>(
  props: CollectionOptions<T, C>,
  factory: CollectionFactory<T, C>,
  context?: unknown,
): C {
  const builder = useMemo(() => new CollectionBuilder<T>(), []);
  const {children, items, collection} = props;
  const result = useMemo(() => {
    if (collection) {
      return collection;
    }
    // @ts-expect-error - Type 'undefined' is not assignable to type 'CollectionChildren<T>'
    const nodes = builder.build({children, items}, context);
    return factory(nodes);
  }, [builder, children, items, collection, context, factory]);
  return result;
}
